import {
  DASHBOARD_SKIP_CACHE,
  DASHBOARD_VERSION_POLL_INTERVAL_MS,
} from "@/core/constants/dashboard.constants";
import { buildDashboardQueryKey } from "@/core/hooks/dashboardQueryKeys";
import { useDashboardInvalidation } from "@/core/hooks/useDashboardInvalidation";
import { useDashboardStore } from "@/core/hooks/useDashboardStore";
import type { DashboardCacheVersions } from "@/core/interfaces/Dashboard";
import { DashboardService } from "@/core/services/Dashboard.service";
import { useQuery } from "@tanstack/vue-query";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

/**
 * Polls /cache-versions ~every 30s while the dashboard page is active.
 * Pauses when the tab is hidden, the user leaves the dashboard, or offline.
 * Does not refetch on window focus.
 */
export function useDashboardCacheVersions(
  options: {
    enabled?: boolean;
    intervalMs?: number;
  } = {}
) {
  const { scope, versions, metaLoaded, setVersions } = useDashboardStore();
  const { applyVersionChanges } = useDashboardInvalidation();

  const isPageActive = ref(true);
  const isTabVisible = ref(
    typeof document !== "undefined"
      ? document.visibilityState === "visible"
      : true
  );
  const isOnline = ref(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const pollingEnabled = ref(
    options.enabled !== false && !DASHBOARD_SKIP_CACHE
  );
  const intervalMs = options.intervalMs ?? DASHBOARD_VERSION_POLL_INTERVAL_MS;

  // Stable key from scope only — avoid extra filter object churn.
  const queryKey = computed(() =>
    buildDashboardQueryKey("cache-versions", scope.value)
  );

  const canPoll = computed(
    () =>
      pollingEnabled.value &&
      metaLoaded.value &&
      isPageActive.value &&
      isTabVisible.value &&
      isOnline.value
  );

  const query = useQuery({
    queryKey,
    queryFn: () =>
      DashboardService.getCacheVersions({
        id_don_vi: scope.value?.idDonVi,
        id_kho: scope.value?.idKho,
      }),
    enabled: canPoll,
    // Freshness is driven by refetchInterval, not staleTime-triggered refetches.
    staleTime: intervalMs,
    gcTime: DASHBOARD_SKIP_CACHE ? 0 : undefined,
    refetchInterval: () => (canPoll.value ? intervalMs : false),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Remount/re-enable only refetches when data is stale (staleTime = poll interval).
    refetchOnMount: true,
    shallow: true,
    throwOnError: false,
  });

  watch(
    () => query.data.value?.data,
    (next) => {
      if (!next || DASHBOARD_SKIP_CACHE) {
        return;
      }
      const previous: DashboardCacheVersions = { ...versions.value };
      const changed =
        next.global !== previous.global ||
        next.map !== previous.map ||
        next.unit !== previous.unit ||
        next.warehouse !== previous.warehouse ||
        next.activities !== previous.activities ||
        next.summary !== previous.summary ||
        next.movement !== previous.movement ||
        next.tickets !== previous.tickets ||
        next.alerts !== previous.alerts ||
        next.topEntities !== previous.topEntities;

      if (!changed) {
        return;
      }

      setVersions(next);
      applyVersionChanges(previous, next);
    }
  );

  function onVisibilityChange(): void {
    isTabVisible.value = document.visibilityState === "visible";
  }

  function onOnline(): void {
    isOnline.value = true;
  }

  function onOffline(): void {
    isOnline.value = false;
  }

  onMounted(() => {
    isPageActive.value = true;
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
  });

  onUnmounted(() => {
    isPageActive.value = false;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
  });

  function pause(): void {
    pollingEnabled.value = false;
  }

  function resume(): void {
    if (!DASHBOARD_SKIP_CACHE) {
      pollingEnabled.value = true;
    }
  }

  return {
    query,
    versions,
    pause,
    resume,
    isTabVisible,
    isOnline,
  };
}
